import type { BadgeTone } from '@zarya/ui';
import { hasKey, t, tn } from '@app/i18n';
import type { components } from '@app/api/types';

/**
 * Vocabulário de T9/T10 — rótulos e tons, num lugar só.
 *
 * Tudo aqui é literal do inventário do protótipo (§2.3, §3.4, §4.2, §4.3) e dos
 * FR-057…FR-068. Nada de rótulo inventado: quem muda texto muda aqui.
 */

export type Approval = components['schemas']['Approval'];
export type ApprovalDetail = components['schemas']['ApprovalDetail'];
export type ApprovalSummary = components['schemas']['ApprovalSummary'];
export type Diff = NonNullable<components['schemas']['Diff']>;
export type DiffChange = components['schemas']['DiffChange'];
export type Severidade = components['schemas']['Severidade'];
export type Provenance = NonNullable<components['schemas']['Provenance']>;
export type DeploymentAfterApproval = components['schemas']['DeploymentAfterApproval'];

/**
 * O nome da identidade na linha de T9 — o mesmo que T14 mostra.
 *
 * A fila é, na maior parte, de identidade RECÉM-DESCOBERTA: ela não tem slug,
 * porque quem nomeia é a atribuição, e a atribuição é justamente o que ainda não
 * aconteceu. Enquanto o `identity_slug` era o único nome, a coluna caía em `—`
 * seguido do UUID cru em 21 linhas (DEF-06), e a MESMA identidade aparecia
 * nomeada em T14 e anônima aqui.
 *
 * A ordem é a da casa (`slug ?? image_basename ?? …`, como T4, T13 e T14): o
 * slug é o nome que a pessoa escolheu, o basename é o nome que o mundo já dava.
 * O `—` fica só para a linha que não tem nenhum dos dois — que é afirmação
 * legítima («nem a imagem sabemos»), e não a regra.
 */
export function identityName(approval: Approval): string {
  return approval.identity_slug ?? approval.identity_image_basename ?? '—';
}

export type ReasonApproval = Approval['reason'];
export type StatusApproval = Approval['status'];
export type OriginApproval = Approval['origin'];
export type Closure = NonNullable<Approval['automatic_closure']>;

/** Tom do badge de motivo (`REASON` do protótipo). */
const REASON_TOM: Record<ReasonApproval, BadgeTone> = {
  nova_identidade: 'primary',
  capabilities_alteradas: 'warning',
};

/**
 * Motivo — rótulo e tom do badge.
 *
 * Função, e não tabela: o rótulo vem do catálogo, e uma tabela de módulo é
 * avaliada na importação — com o seletor de idioma no ar ela seguiria mostrando
 * o idioma anterior sem nada ficar vermelho. A chave é o valor do enum, em
 * snake_case, pela mesma razão que `error.*` é chaveado pelo `code` do envelope.
 */
export function reason(valor: ReasonApproval): { rotulo: string; tone: BadgeTone } {
  return { rotulo: t(`approvals.reason.${valor}`), tone: REASON_TOM[valor] };
}

// --- o que a pendência afirma sobre a mudança (30/08) ---------------------------

/**
 * A leitura da pendência: **uma** resposta para "o que mudou nesta release?", da
 * qual saem todos os rótulos de T10.
 *
 * # POR QUE ISTO EXISTE
 *
 * Até 30/08 os três rótulos do drawer vinham de três fontes, e elas discordavam
 * na mesma tela (ensaio de 30/08, cenas 4 e 8):
 *
 *   - o alerta acima da dobra saía de `motivo`, que responde "identidade nova ou
 *     já existente?" — e por isso anunciava «Capabilities alteradas» sobre uma
 *     release que só trocou o binário;
 *   - «Mudança de código» era **fixo em SIM**, herdado do protótipo, cuja premissa
 *     era "release que só muda código não passa por aqui" (inventário §2.3). Ela
 *     deixou de valer duas vezes: a fila recebe a release só de código (para
 *     fechá-la sozinha, ou quando o auto falhou) e recebe o candidato de T14, que
 *     não tem código nenhum — é a **mesma** release já no ar;
 *   - «Mudança de autoridade» saía de `classificacao === AUTHORITY_CHANGE`. O
 *     executor classifica **redução** como `CODE_CHANGE` (ela não escala poder),
 *     então uma pendência que tira duas capabilities dizia «Mudança de autoridade:
 *     NÃO» duas linhas abaixo do diff que mostrava as duas saindo.
 *
 * A classificação do executor está certa nos três casos; o que mentia era o texto.
 * Por isso a correção é esta função, e não uma regra nova: os rótulos passam a
 * sair todos daqui, e não podem mais divergir entre si.
 *
 * # A REGRA
 *
 * É a mesma leitura do domínio (`domain.DecidirAutomaticamente`): «só código» é
 * `CODE_CHANGE` **e não** redução — a combinação que fecha sozinha em qualquer
 * ambiente, inclusive produção. Tudo o mais mexeu na lista de capabilities.
 *
 * `classificacao` nula é `nao_verificada`, e não «NÃO»: é o `diff_indisponivel` —
 * o executor não disse o que mudou. Afirmar "nenhuma autoridade mudou" ali seria
 * a tela respondendo por ele.
 */
export type PendencyChange =
  'nova_identidade' | 'autoridade' | 'reducao' | 'so_codigo' | 'nao_verificada';

export function pendencyChange(
  a: Pick<Approval, 'reason' | 'classification' | 'reduction'>,
): PendencyChange {
  if (a.reason === 'nova_identidade') return 'nova_identidade';
  if (!a.classification) return 'nao_verificada';
  if (a.classification === 'AUTHORITY_CHANGE') return 'autoridade';
  return a.reduction ? 'reducao' : 'so_codigo';
}

/**
 * A release traz binário novo?
 *
 * Só o discovery cria pendência a partir de uma imagem nova no registry. O
 * candidato de T14 (`access-control`) e a redução automática de FR-036 reaproveitam
 * a release **vigente** — o mesmo digest que já está no ar —, e por isso o rodapé
 * dizia «Mudança de código: SIM» sobre uma decisão em que código nenhum mudou.
 */
export function changedCode(a: Pick<Approval, 'origin'>): boolean {
  return a.origin === 'discovery';
}

export interface ChangeLabels {
  /** Badge do cabeçalho do drawer. */
  rotulo: string;
  tone: BadgeTone;
  /** Alerta acima da dobra (FR-061). */
  titulo: string;
  alertaTone: 'info' | 'warning' | 'danger';
  /** Rodapé do diff: `SIM` · `SIM (redução)` · `NÃO` · `não verificado`. */
  autoridade: string;
  /** `true` destaca, `false` acalma, `null` não afirma nada. */
  autoridadeSim: boolean | null;
  /** Frase do diff quando ele não tem linhas. */
  semMudancas: string;
}

/**
 * Os rótulos de cada leitura. Nada aqui se calcula em outro lugar — quem muda
 * texto muda nesta tabela, e as três posições da tela mudam juntas.
 */
/** As cinco leituras, na ordem em que aparecem no código. Existe para o teste
 * iterar: sem ela, uma leitura nova entraria sem rótulo e sem ninguém cobrar. */
export const PENDENCY_CHANGES: PendencyChange[] = [
  'nova_identidade',
  'autoridade',
  'reducao',
  'so_codigo',
  'nao_verificada',
];

const CHANGE_TOM: Record<
  PendencyChange,
  { tone: BadgeTone; alertaTone: ChangeLabels['alertaTone']; autoridadeSim: boolean | null }
> = {
  nova_identidade: { tone: 'primary', alertaTone: 'info', autoridadeSim: true },
  autoridade: { tone: 'warning', alertaTone: 'warning', autoridadeSim: true },
  // O parêntese do rótulo («sim (redução)») é o que a classificação sozinha não
  // diz. Sem ele o rodapé afirmaria «NÃO» sobre um diff de capabilities
  // removidas, que foi o defeito de 30/08.
  reducao: { tone: 'success', alertaTone: 'info', autoridadeSim: true },
  so_codigo: { tone: 'neutral', alertaTone: 'info', autoridadeSim: false },
  nao_verificada: { tone: 'neutral', alertaTone: 'warning', autoridadeSim: null },
};

/**
 * Os rótulos de cada leitura. Nada aqui se calcula em outro lugar — quem muda
 * texto muda no catálogo (`approvals.change.*`), e as três posições da tela
 * mudam juntas.
 */
export function change(leitura: PendencyChange): ChangeLabels {
  const tons = CHANGE_TOM[leitura];
  return {
    rotulo: t(`approvals.change.${leitura}.label`),
    titulo: t(`approvals.change.${leitura}.title`),
    autoridade: t(`approvals.change.${leitura}.authority`),
    semMudancas: t(`approvals.change.${leitura}.noChanges`),
    ...tons,
  };
}

/**
 * O corpo do alerta acima da dobra.
 *
 * Depende de `origem` porque a frase muda de sujeito: no discovery quem mudou foi
 * o cliente, no `agent.yaml` de uma release nova; em T14 quem mudou foi alguém
 * nesta plataforma, sobre a release que já está no ar. Dizer «o cliente mudou o
 * agent.yaml» sobre um pedido feito no Access Control aponta para o lugar errado
 * quem for conferir.
 */
export function changeText(
  a: Pick<Approval, 'reason' | 'origin' | 'classification' | 'reduction' | 'base_contract_version'>,
): string {
  const current = a.base_contract_version ? ` v${a.base_contract_version}` : '';
  const daEsteira = changedCode(a);

  switch (pendencyChange(a)) {
    case 'nova_identidade':
      return t('approvals.changeText.nova_identidade');
    case 'autoridade':
      return t(
        daEsteira
          ? 'approvals.changeText.autoridade.pipeline'
          : 'approvals.changeText.autoridade.accessControl',
        { current },
      );
    case 'reducao':
      return t('approvals.changeText.reducao', { current });
    case 'so_codigo':
      return t('approvals.changeText.so_codigo', { current });
    case 'nao_verificada':
      return t('approvals.changeText.nao_verificada');
  }
}

/**
 * Status exibido. `auto` ganha o qualificador do fechamento: "auto · só código"
 * e "auto · já coberta" são coisas diferentes no Histórico (FR-065, FR-066).
 */
export const CLOSURES: Closure[] = [
  'code_only', 'capability_removed', 'environment_exempt', 'automatic_onboarding',
  'already_covered', 'resource_removed', 'superseded', 'trusted_requester',
  'superseded_by_trusted_requester',
];

/** Rótulo do fechamento automático, do catálogo. */
export function closureLabel(fechamento: Closure): string {
  return t(`approvals.closure.${fechamento}`);
}

export function statusLabel(a: Pick<Approval, 'status' | 'automatic_closure'>): string {
  if (a.status !== 'auto') return t(`approvals.status.${a.status}`);
  const fechamento = a.automatic_closure ? closureLabel(a.automatic_closure) : null;
  return fechamento
    ? t('approvals.status.autoWith', { closure: fechamento })
    : t('approvals.status.auto');
}

export function statusTone(status: StatusApproval): BadgeTone {
  if (status === 'rejeitada') return 'danger';
  if (status === 'pendente') return 'warning';
  return 'success';
}

/** Origem (chip). `reducao_automatica` vem da FR-036, não do protótipo. */
export function originLabel(origem: OriginApproval): string {
  return t(`approvals.origin.${origem}`);
}

/**
 * Severidade agregada e por mudança (D4, FR-060). `nao_catalogado` **domina**:
 * é o único que ganha destaque próprio na tabela de diff.
 */
const SEVERITY_TOM: Record<Severidade, BadgeTone> = {
  high: 'danger',
  write: 'warning',
  read: 'success',
  nao_catalogado: 'danger',
  desconhecido: 'neutral',
  nenhuma: 'success',
};

export function severity(valor: Severidade): { rotulo: string; tone: BadgeTone } {
  return { rotulo: t(`approvals.severity.${valor}`), tone: SEVERITY_TOM[valor] };
}

/** Explicação de `nao_catalogado` — literal de 5.12 / FR-060. */
export function notCatalogedTip(): string {
  return t('approvals.notCataloged.tip');
}

/** ⓘ de FR-058, em T9 e T10. */
export function otherPendencyTip(): string {
  return t('approvals.otherPendency.tip');
}

// --- DEF-39: o alvo declarado que não vira delegação (09/09) --------------------

export function unresolvedAgentsTitle(n: number): string {
  return tn('approvals.unresolvedAgents.title', n);
}

/**
 * Uma linha, com os slugs, o motivo e a consequência.
 *
 * As três coisas juntas porque é assim que a informação é acionável na hora da
 * decisão: **quais** alvos (o slug é o que o autor da release escreveu), **por
 * quê** (não há identidade governada com esse slug aqui) e **o que acontece se
 * eu aprovar** (o contrato sai sem eles). Sem a terceira, o aviso vira uma
 * pergunta; sem a segunda, parece defeito do produto.
 *
 * Não diz «atribua a identidade» como ordem: o slug pode estar errado na
 * release, e mandar criar identidade para um erro de digitação seria pior que
 * calar.
 */
export function unresolvedAgentsText(slugs: string[]): string {
  return tn('approvals.unresolvedAgents.text', slugs.length, { slugs: slugs.join(', ') });
}

/**
 * Selo «redução» (FR-078, nota de 29/08) — o rótulo e o ⓘ, num lugar só.
 *
 * A explicação vai junto porque o selo é contraintuitivo na fila: a linha tem
 * severidade `nenhuma` e classificação `CODE_CHANGE`, e sem o texto quem olha
 * pergunta por que ela está esperando decisão. A resposta é o ambiente — em
 * produção, tirar poder também passa por gente.
 */
export function reductionLabel(): string {
  return t('approvals.reduction.label');
}

export function reductionTip(): string {
  return t('approvals.reduction.tip');
}

/** Severidade exibida quando a identidade não está atribuída (FR-050, cenário 4.9). */
export function pendingAssignment(): string {
  return t('approvals.pendingAssignment');
}

/** Os 8 tipos de mudança do diff (FR-060), com o sinal do protótipo (`+ ~ -`). */
const KIND_SINAL: Record<DiffChange['kind'], '+' | '~' | '-'> = {
  capability_added: '+',
  capability_removed: '-',
  limit_loosened: '~',
  limit_tightened: '~',
  a2a_added: '+',
  a2a_removed: '-',
  credential_added: '+',
  credential_removed: '-',
};

export function kindChange(tipo: DiffChange['kind']): { rotulo: string; sinal: '+' | '~' | '-' } {
  return { rotulo: t(`approvals.kindChange.${tipo}`), sinal: KIND_SINAL[tipo] };
}

/**
 * As linhas de TOOL do diff (`<apim_id>/<tool>`): as duas de capability e as duas de limite.
 * São elas que mostram a tool como T5 e T7 (aim-92c); A2A e credencial não são tool.
 * Espelha `ChangeKind.IsTool` do backend, que é quem preenche `tool_display_name`.
 */
const TOOL_KINDS: ReadonlySet<DiffChange['kind']> = new Set([
  'capability_added',
  'capability_removed',
  'limit_loosened',
  'limit_tightened',
]);

export function isToolChange(tipo: DiffChange['kind']): boolean {
  return TOOL_KINDS.has(tipo);
}

/** Blocos de procedência exibidos em T10 (FR-043, FR-059). */
export const BLOCOS_PROVENANCE = ['scan', 'sbom', 'signature'] as const;

export function provenanceBlockLabel(chave: (typeof BLOCOS_PROVENANCE)[number]): string {
  return t(`approvals.provenance.block.${chave}`);
}

/**
 * Chip de emulação de um bloco de procedência: `emulado` / `real` /
 * "não informado" (5.1). `emulated` vem derivado da `ferramenta`/`metodo` pelo
 * backend; `null` = não informado.
 */
export function chipEmulacao(block: components['schemas']['ProvenanceBloco'] | undefined): {
  rotulo: string;
  tone: BadgeTone;
} {
  if (!block || block.emulated === null || block.emulated === undefined) {
    return { rotulo: t('approvals.provenance.notInformed'), tone: 'neutral' };
  }
  return block.emulated
    ? { rotulo: t('approvals.provenance.emulated'), tone: 'warning' }
    : { rotulo: t('approvals.provenance.real'), tone: 'success' };
}

/**
 * Resumo legível de um bloco opaco de procedência (`{ferramenta, resultado}`,
 * `{formato, componentes}`, `{assinado, metodo}`…). O contrato só garante
 * `emulated`; o resto é exibido como veio.
 */
export function summaryBlock(block: components['schemas']['ProvenanceBloco'] | undefined): string {
  if (!block) return '—';
  // O bloco é opaco (a esteira escreve o que quiser): mostramos o que informa
  // algo — chaves com valor — e escondemos o que é metadado da leitura
  // (`bruto`, `emulated`) ou vazio. Chaves conhecidas ganham rótulo pt-BR.
  const CONHECIDAS = [
    'resultado',
    'ferramenta',
    'metodo',
    'formato',
    'componentes',
    'assinado',
    'erro',
  ];
  const parts = Object.entries(block)
    .filter(([chave, valor]) => {
      if (chave === 'emulated' || chave === 'bruto') return false;
      if (valor === null || valor === undefined || valor === '') return false;
      if (typeof valor === 'object') return false;
      return true;
    })
    .map(([chave, valor]) => {
      const texto =
        typeof valor === 'boolean'
          ? t(valor ? 'approvals.provenance.yes' : 'approvals.provenance.no')
          : String(valor);
      const rotulo = CONHECIDAS.includes(chave) ? t(`approvals.provenance.field.${chave}`) : chave;
      return `${rotulo}: ${texto}`;
    });
  return parts.length ? parts.join(' · ') : '—';
}

/** Decisor exibido: e-mail, `plataforma` (auto) ou `—`. */
export function decisorLabel(a: Pick<Approval, 'decided_by' | 'status'>): string {
  if (a.decided_by) return a.decided_by;
  return a.status === 'auto' ? t('approvals.decider.platform') : '—';
}

/** Motivo escrito pela pessoa ou identificação genérica da decisão automática. A regra histórica é exibida pelo snapshot. */
export function reasonDecisionLabel(a: Pick<Approval, 'decision_reason' | 'status'>): {
  texto: string;
  vazio: boolean;
} {
  if (a.decision_reason) return { texto: a.decision_reason, vazio: false };
  return {
    texto: a.status === 'auto' ? t('approvals.decisionReason.auto') : '—',
    vazio: true,
  };
}

/** Digest curto para tabela — o completo (copiável) fica no drawer (FR-059). */
export function digestShort(digest?: string | null): string {
  if (!digest) return '—';
  const [algo, hex] = digest.split(':');
  return hex ? `${algo}:${hex.slice(0, 12)}…` : digest;
}

/**
 * «comparada com o contrato v{N}» / «primeira release — tudo é autoridade nova»
 * (ajustes-front #11): a fila T9 e o cabeçalho T10 dizem contra o que a release
 * está sendo comparada — sem isso v2 e v3 da mesma identidade eram iguais na tela.
 */
export function comparadaWith(contratoBaseVersion: number | null | undefined): string {
  return contratoBaseVersion != null
    ? t('approvals.comparedWith', { version: contratoBaseVersion })
    : t('approvals.comparedWith.first');
}

// --- agrupamento da fila por identidade (29/08) ---------------------------------

/** Selo da linha que está rodando no cluster. */
export function inExecution(): string {
  return t('approvals.inExecution');
}

/** ⓘ do selo — por que esta linha é a que importa. */
export function inExecutionTip(): string {
  return t('approvals.inExecution.tip');
}

/** Rótulo do grupo recolhido. */
export function otherReleases(n: number): string {
  return t('approvals.otherReleases', { n });
}

export interface IdentityGroup {
  /** Chave estável: `identity_id`. */
  identityId: string;
  /** A linha que abre o grupo: a que está em execução, ou a de build mais recente. */
  principal: Approval;
  /** As demais, recolhidas atrás de «Outras releases desta identidade (N)». */
  outras: Approval[];
}

/**
 * Agrupa a fila **por identidade** e escolhe qual pendência abre cada grupo
 * (29/08).
 *
 * # O PROBLEMA
 *
 * No ACR do lab uma imagem tem dezenas de tags, e o discovery criava uma pendência
 * por digest. O aprovador via N linhas do mesmo agente, idênticas na tela, e a
 * única forma de achar a que importa era copiar o digest de cada uma e comparar
 * com o Runtime Status — à mão, fora desta tela.
 *
 * # A REGRA
 *
 * A principal é **a que está rodando no cluster** (`em_execucao === true`). É a
 * única sobre a qual há uma pergunta urgente: aquele digest está executando agora
 * sem contrato aprovado. Quando nenhuma está — ou quando ninguém leu o cluster e
 * `em_execucao` é `null` —, a principal é a de **build mais recente**, que é a
 * próxima decisão natural.
 *
 * O desempate por `criada_em` no fim não é detalhe: sem ele, duas releases sem
 * data de build trocariam de lugar entre dois carregamentos da mesma tela.
 *
 * A ordem **entre** grupos preserva a da fila (mais antigas primeiro, AC-26): o
 * agrupamento reorganiza dentro da identidade, não a prioridade entre agentes.
 */
export function groupByIdentity(itens: Approval[]): IdentityGroup[] {
  const byIdentity = new Map<string, Approval[]>();
  for (const a of itens) {
    const current = byIdentity.get(a.identity_id);
    if (current) current.push(a);
    else byIdentity.set(a.identity_id, [a]);
  }

  return [...byIdentity.entries()].map(([identityId, linhas]) => {
    const sortedCapabilities = [...linhas].sort(comparePendencies);
    const [principal, ...other] = sortedCapabilities;
    return { identityId, principal, outras: other };
  });
}

/** Ordem dentro de um grupo: em execução → build mais recente → mais antiga na fila. */
function comparePendencies(a: Approval, b: Approval): number {
  const execution = Number(b.running === true) - Number(a.running === true);
  if (execution !== 0) return execution;

  const build = instant(b.release_built_at) - instant(a.release_built_at);
  if (build !== 0) return build;

  return instant(a.created_at) - instant(b.created_at);
}

/**
 * Instante comparável de uma data ISO. Ausente e inválida viram `0` — o piso, que
 * põe a release sem data do build **depois** das que têm, e nunca no topo. Uma
 * release cuja procedência não trouxe data não pode se apresentar como a mais
 * recente só por não ter dito nada.
 */
function instant(iso: string | null | undefined): number {
  if (!iso) return 0;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

/**
 * Detalhe de uma linha do diff (T10), pela `code` + `params` do contrato (marco 3).
 *
 * A frase nascia no backend (`usecases/declaration/a2a.go`) e ficava em português no
 * console em inglês. Agora o backend manda o motivo em código; a frase sai do catálogo
 * no idioma ativo. Sem `code` (linha do executor, ou aprovação gravada antes do marco 3),
 * ou com código que o catálogo ainda não conhece, vale `detail` — a frase do servidor,
 * nunca a chave crua.
 */
/**
 * O que a aprovação fez com a imagem (P28), como sufixo da mensagem de sucesso.
 *
 * Ausente devolve texto vazio, e é o caso da maioria: o ambiente está em `manual` e a frase
 * tem de continuar a ser a de sempre. Ausência não é «não subiu» — é «este ambiente não sobe
 * sozinho», e inventar uma frase para ela poria uma informação nova em toda aprovação de todo
 * ambiente que não pediu nada.
 *
 * A falha é dita na mesma frase do sucesso da aprovação de propósito: é exatamente essa a
 * situação — aprovou, e a imagem não subiu. Quem lê precisa das duas metades juntas.
 */
export function rolloutSuffix(deployment: DeploymentAfterApproval | undefined): string {
  if (!deployment) return '';
  if (deployment.status === 'failed') {
    return ` · ${t('approvals.drawer.deployment.failed', { code: deployment.code ?? '' })}`;
  }
  return ` · ${t(`approvals.drawer.deployment.${deployment.status}`)}`;
}

export function changeDetail(
  change: Pick<DiffChange, 'detail' | 'code' | 'params'>,
): string | null {
  const key = change.code ? `approvals.diff.detail.${change.code}` : null;
  if (key && hasKey(key)) {
    const params: Record<string, string> = { ...(change.params ?? {}) };
    // `role` vazio = identidade sem papel: o backend manda cru, a palavra é nossa.
    if (params.role === '') params.role = t('approvals.diff.detail.noRole');
    return t(key, params);
  }
  return change.detail ?? null;
}
